import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Layers,
    ClipboardList,
    CreditCard,
    Settings,
    LogOut,
    Plus,
    Sun,
    Moon,
    ChevronLeft,
    ChevronRight,
    PanelLeftClose,
    PanelLeftOpen,
    Menu,
    Headphones,
    Flame,
    GraduationCap,
    Facebook
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { getUserPlanType, hasAudioAccess, hasExamAccess, FREE_PLAN_LIMITS } from '../../utils/subscription';
import { useEffect } from 'react';
import LanguageSwitcher from '../LanguageSwitcher';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface DashboardLayoutProps {
    children: React.ReactNode;
    user?: any;
}

export default function DashboardLayout({ children, user }: DashboardLayoutProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const [subscription, setSubscription] = useState<any>(null);
    const [freeLimits, setFreeLimits] = useState({ flashcards: false, quiz: false });

    useEffect(() => {
        if (user) {
            fetchLimits();
        }
    }, [user]);

    const fetchLimits = async () => {
        try {
            // Fetch subscription
            // Fetch subscriptions (handle multiple active subs safely)
            const { data: subs } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .eq('status', 'active');

            let effectiveSub = null;
            if (subs && subs.length > 0) {
                // If any is Premium, take it. Otherwise take the first one (Standard)
                effectiveSub = subs.find((s: any) => s.plan_name === 'Premium') || subs[0];
            }

            setSubscription(effectiveSub);

            // If free (no effective sub or explicit free status), check limits
            if (!effectiveSub || getUserPlanType(effectiveSub) === 'free') {
                const { data: categories } = await supabase
                    .from('categories')
                    .select('id')
                    .order('id', { ascending: true })
                    .limit(1);

                if (categories && categories.length > 0) {
                    const { data: progress } = await supabase
                        .from('user_progress')
                        .select('flashcards_completed, qcm_completed, vrai_faux_completed')
                        .eq('user_id', user.id)
                        .eq('category_id', categories[0].id)
                        .maybeSingle();

                    if (progress) {
                        setFreeLimits({
                            flashcards: progress.flashcards_completed >= FREE_PLAN_LIMITS.maxFlashcards,
                            quiz: (progress.qcm_completed || 0) >= FREE_PLAN_LIMITS.maxQuiz &&
                                (progress.vrai_faux_completed || 0) >= FREE_PLAN_LIMITS.maxQuiz
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching layout data:', error);
        }
    };

    const navigation = [
        { name: t('dashboard.nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
        { name: t('dashboard.nav.quiz'), href: '/quiz', icon: ClipboardList },
        { name: t('dashboard.nav.flashcards'), href: '/flashcards', icon: Layers },
        { name: t('dashboard.nav.audio'), href: '/audio', icon: Headphones },
        { name: t('dashboard.nav.exam'), href: '/exam', icon: GraduationCap },
        { name: t('dashboard.nav.subscription'), href: '/subscription', icon: CreditCard },
    ];

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    const isPathDisabled = (href: string) => {
        if (href === '/flashcards' && freeLimits.flashcards) return true;
        if (href === '/quiz' && freeLimits.quiz) return true;
        if (href === '/audio' && !hasAudioAccess(subscription)) return true;
        if (href === '/exam' && !hasExamAccess(subscription)) return true;
        return false;
    };

    return (
        <div className="h-screen bg-dark-bg flex overflow-hidden">
            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 bg-dark-card border-r border-dark-border transition-all duration-300 ease-in-out transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    } lg:translate-x-0 lg:relative flex-none ${isCollapsed ? 'w-20' : 'w-64'}`}
            >
                <div className="h-full flex flex-col">
                    {/* Logo & Toggle Container */}
                    <div className={`p-6 flex items-center gap-3 ${isCollapsed ? 'px-4 justify-center' : 'justify-between'}`}>
                        <div className="flex items-center gap-3 overflow-hidden">
                            <div className="w-10 h-10 shrink-0">
                                <img src="/logo/app-logo.png" alt="CitoyenPro Logo" className="w-full h-full object-contain" />
                            </div>
                            {!isCollapsed && (
                                <span className="text-xl font-bold text-text-main whitespace-nowrap animate-in fade-in slide-in-from-left-4 duration-300">
                                    CitoyenPro
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className="p-1.5 rounded-lg text-text-muted hover:bg-dark-hover hover:text-text-main transition-all duration-200"
                            title={isCollapsed ? t('dashboard.sidebar.expand') : t('dashboard.sidebar.collapse')}
                        >
                            {isCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
                        </button>
                    </div>

                    <nav className="flex-1 px-4 space-y-2 mt-4 overflow-y-auto overflow-x-hidden custom-scrollbar">
                        {navigation.map((item) => {
                            const isActive = location.pathname.startsWith(item.href);
                            const Icon = item.icon;
                            const isDisabled = isPathDisabled(item.href);

                            return (
                                <Link
                                    key={item.href}
                                    to={isDisabled ? '/pricing' : item.href}
                                    title={isCollapsed ? item.name : ''}
                                    className={`flex items-center gap-3 py-3 rounded-xl transition-all duration-200 group ${isCollapsed ? 'px-0 justify-center' : 'px-4'
                                        } ${isActive
                                            ? 'bg-primary/10 text-primary'
                                            : 'text-text-muted hover:bg-dark-hover hover:text-text-main font-medium'
                                        }`}
                                >
                                    <Icon size={20} className={isActive ? 'text-primary' : 'group-hover:text-text-main'} />
                                    {!isCollapsed && <span className="whitespace-nowrap">{item.name}</span>}
                                    {isDisabled && !isCollapsed && (
                                        <div className="ml-auto">
                                            <span className="text-[9px] bg-red-500/20 text-red-500 px-1.5 py-0.5 rounded border border-red-500/20 font-bold">{t('dashboard.learning.pro')}</span>
                                        </div>
                                    )}
                                    {isActive && !isCollapsed && !isDisabled && (
                                        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(220,38,38,0.5)]" />
                                    )}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Bottom Actions */}
                    <div className="p-4 border-t border-dark-border space-y-2">
                        <Link
                            to="/settings"
                            title={isCollapsed ? t('dashboard.nav.settings') : ''}
                            className={`w-full flex items-center gap-3 py-3 rounded-xl transition-all ${isCollapsed ? 'px-0 justify-center' : 'px-4'
                                } ${location.pathname === '/settings'
                                    ? 'bg-primary/10 text-primary font-bold'
                                    : 'text-text-muted hover:bg-dark-hover hover:text-text-main font-medium'
                                }`}>
                            <Settings size={20} className={location.pathname === '/settings' ? 'text-primary' : ''} />
                            {!isCollapsed && <span className="font-medium">{t('dashboard.nav.settings')}</span>}
                        </Link>


                        {/* User Profile */}
                        <div className={`mt-4 p-3 bg-dark-hover rounded-xl flex items-center gap-3 ${isCollapsed ? 'px-0 justify-center' : ''}`}>
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold border border-primary/30 shrink-0">
                                {user?.email?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            {!isCollapsed && (
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-text-main truncate">
                                        {user?.user_metadata?.full_name || t('dashboard.profile.user')}
                                    </p>
                                    <p className="text-xs text-text-muted truncate">
                                        {user?.email || 'email@example.com'}
                                    </p>
                                </div>
                            )}
                            {!isCollapsed && (
                                <button
                                    onClick={handleLogout}
                                    className="p-1.5 hover:bg-red-500/10 hover:text-red-500 rounded-lg transition-colors text-text-muted"
                                    title={t('dashboard.nav.logout')}
                                >
                                    <LogOut size={18} />
                                </button>
                            )}
                        </div>
                        {isCollapsed && (
                            <button
                                onClick={handleLogout}
                                className="w-full flex justify-center p-2 hover:bg-red-500/10 hover:text-red-500 rounded-lg transition-colors text-text-muted mt-2"
                                title={t('dashboard.nav.logout')}
                            >
                                <LogOut size={18} />
                            </button>
                        )}
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col h-screen overflow-hidden">
                {/* Top Header */}
                <header className="h-16 bg-dark-bg/80 backdrop-blur-md border-b border-dark-border z-40 flex-none px-6">
                    <div className="h-full flex items-center justify-between">
                        {/* Mobile Menu Toggle */}
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="lg:hidden p-2 -ml-2 text-text-muted hover:text-text-main"
                        >
                            <Menu size={24} />
                        </button>

                        {/* Search Bar placeholder */}
                        <div className="hidden md:flex items-center gap-2 px-4 w-96">
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                            <LanguageSwitcher />
                            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-lg text-orange-500 font-bold text-sm">
                                <Flame size={18} fill="currentColor" />
                                <span>{user?.user_metadata?.streak_count || 0}</span>
                            </div>
                            <button
                                onClick={toggleTheme}
                                className="p-2.5 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                                title={theme === 'dark' ? t('dashboard.sidebar.mode_light') || 'Mode clair' : t('dashboard.sidebar.mode_dark') || 'Mode sombre'}
                            >
                                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                            </button>

                            <Link
                                to="/quiz"
                                className="hidden md:flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg shadow-primary/20"
                            >
                                <Plus size={20} />
                                <span>{t('dashboard.nav.new_quiz')}</span>
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Scrollable Content */}
                <main className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                    {children}
                </main>
            </div>
        </div>
    );
}
